import React, { createContext, useContext, ReactNode } from 'react';
import { analytics } from '../firebaseConfig';
import useAnalytics from '../hooks/useAnalytics';

// Define context type
interface AnalyticsContextType {
  trackButton: (buttonName: string, buttonLocation: string) => void;
  trackSection: (sectionName: string) => void;
  trackEvent: (eventName: string, eventParams: Record<string, any>) => void;
  isAnalyticsAvailable: boolean;
}

// Create context with default values
const AnalyticsContext = createContext<AnalyticsContextType>({
  trackButton: () => {},
  trackSection: () => {},
  trackEvent: () => {},
  isAnalyticsAvailable: false,
});

interface AnalyticsProviderProps {
  children: ReactNode;
}

/**
 * Analytics Provider component to make tracking functions available throughout the app
 */
export const AnalyticsProvider: React.FC<AnalyticsProviderProps> = ({ children }) => {
  // Use our custom hook for analytics
  const { trackButton, trackSection, trackEvent } = useAnalytics();
  
  // Check if analytics is available
  const isAnalyticsAvailable = !!analytics;
  
  // Create context value
  const contextValue: AnalyticsContextType = {
    trackButton,
    trackSection,
    trackEvent,
    isAnalyticsAvailable,
  };
  
  return (
    <AnalyticsContext.Provider value={contextValue}>
      {children}
    </AnalyticsContext.Provider>
  );
};

/**
 * Custom hook to use analytics from any component
 */
export const useAnalyticsContext = () => {
  const context = useContext(AnalyticsContext);
  
  if (!context) {
    throw new Error('useAnalyticsContext must be used within an AnalyticsProvider');
  }
  
  return context;
};

export default AnalyticsProvider; 